import { useMemo, useState } from 'react'
import { App as AntdApp, Alert, Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd'
import type { TableProps } from 'antd'
import { useAuthStore } from '@/stores/authStore'
import {
  canManageKnowledge,
  KNOWLEDGE_PROBLEM_TYPE_COLOR,
  withUnclassifiedProduct,
} from '@/modules/knowledge/constants'
import {
  useBatchArchiveKnowledge,
  useBatchPublishKnowledge,
  useKnowledgeProducts,
  useKnowledgeReviewFacets,
  useKnowledgeReviewQueue,
  useKnowledgeReviewReasonCodes,
  useKnowledgeReviewSummary,
} from '@/modules/knowledge/queries'
import ErrorResult from '@/components/feedback/ErrorResult'
import KnowledgeDetailDrawer from './components/KnowledgeDetailDrawer'
import KnowledgeEditModal from './components/KnowledgeEditModal'
import KnowledgeReviewNoteModal from './components/KnowledgeReviewNoteModal'
import type { KnowledgeEntry, KnowledgeReviewBand, KnowledgeReviewFacetOption } from '@/types/knowledge'

const { Text, Title } = Typography

// 信号的可读标签与配色。审核者按这些标签挑活干：
// 先清 ai_unverified（高风险），再处理 not_qualified（缺三要素），最后批量 publish_ready。
const SIGNAL_META: Record<string, { label: string; color: string; hint: string }> = {
  ai_unverified: { label: 'AI 归纳待核对', color: 'red', hint: 'AI 产出，必须人工核对后才能发布' },
  not_qualified: {
    label: '三要素不全',
    color: 'volcano',
    hint: '缺"有发生/有问题分析/有解决方案"中的至少一项，不应直接发布',
  },
  missing_solution: { label: '缺解决方案', color: 'orange', hint: '没有可复用的处理办法' },
  missing_analysis: { label: '缺问题分析', color: 'gold', hint: '没有针对问题/产品的分析' },
  missing_occurrence: { label: '缺发生依据', color: 'gold', hint: '看不出这事真的发生过' },
  publish_ready: { label: '可发布', color: 'green', hint: '三要素齐全且非重复簇，可批量发布' },
  no_conclusion: { label: '无处理结论', color: 'orange', hint: '来源侧没写解决方案，价值低' },
  no_root_cause: { label: '无根因', color: 'gold', hint: '缺少原因记录' },
  unclassified_product: { label: '产品未归类', color: 'blue', hint: '没有归属到任何彩页产品' },
  thin_content: { label: '正文过薄', color: 'default', hint: '产品通用知识正文过短，信息量不足' },
  possible_duplicate: { label: '疑似重复', color: 'purple', hint: '与同簇条目标题高度相似，需人工决定留哪条' },
  no_public_version: {
    label: '未脱敏',
    color: 'magenta',
    hint: '还没生成对外版本（发布时会自动补齐），详情抽屉里可预览',
  },
}

const SIGNAL_OPTIONS = [
  { value: '', label: '全部待审' },
  ...Object.entries(SIGNAL_META).map(([value, meta]) => ({
    value,
    label: `${meta.label}（${meta.hint}）`,
  })),
]

// 打分分级的配色。high 用绿色但**不是**"可以自动发布"的意思 —— 见页面上的说明。
const BAND_COLOR: Record<KnowledgeReviewBand, string> = {
  high: 'green',
  mid: 'gold',
  low: 'default',
}

export default function KnowledgeReviewPage() {
  const { message } = AntdApp.useApp()
  const roles = useAuthStore((s) => s.roles)
  const canManage = canManageKnowledge(roles)

  const [signal, setSignal] = useState<string>('')
  // 打分分级过滤：审核者可以只看"建议通过"的，批量过一遍
  const [band, setBand] = useState<KnowledgeReviewBand | undefined>(undefined)
  // 分类维度：审核者先选定"我要审哪一类"，再逐条看；否则几千条混在一起只能靠猜
  const [productKey, setProductKey] = useState<string | undefined>(undefined)
  const [kind, setKind] = useState<string | undefined>(undefined)
  const [problemType, setProblemType] = useState<string | undefined>(undefined)
  const [sourceType, setSourceType] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [detailEntry, setDetailEntry] = useState<KnowledgeEntry | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null)

  const summaryQuery = useKnowledgeReviewSummary(canManage)
  const facetsQuery = useKnowledgeReviewFacets(signal || undefined, canManage)
  // 编辑弹窗的产品下拉需要**全量彩页清单**（与状态无关），与知识条目页同一数据源
  const productOptionsQuery = useKnowledgeProducts()
  const queueQuery = useKnowledgeReviewQueue(
    {
      signal: signal || undefined,
      band,
      product_key: productKey,
      kind,
      problem_type: problemType,
      source_type: sourceType,
      page,
      page_size: pageSize,
    },
    canManage,
  )
  const reasonCodesQuery = useKnowledgeReviewReasonCodes(canManage)
  const batchPublish = useBatchPublishKnowledge()
  const batchArchive = useBatchArchiveKnowledge()

  // 改了任何一个分类条件都要回到第一页并清空选择 —— 否则会误操作到上一批选中的条目
  const resetPaging = () => {
    setPage(1)
    setSelectedIds([])
  }

  // 审核者最常做的动作不是"发布/退回"，而是"先把这条改对再发布"（补解决方案、改分类）。
  // 因此编辑要就地弹窗，而不是提示"请去另一个页面改" —— 那等于没有编辑功能。
  const openEdit = (entry: KnowledgeEntry) => {
    setEditingEntry(entry)
    setEditOpen(true)
    // 关掉抽屉：两个浮层叠在一起时，保存后看不出结果落在哪一条上
    setDetailEntry(null)
  }

  // 下拉选项带计数：光看分类名不知道哪类还有活干
  const withCounts = (options: KnowledgeReviewFacetOption[] | undefined) =>
    (options ?? []).map((item) => ({
      value: item.value,
      label: `${item.label}（${item.count}）`,
    }))

  // 当前选中的分类，拼成"我在审哪一类"的横幅
  const facets = facetsQuery.data
  const scoring = summaryQuery.data?.scoring
  const bandCounts = summaryQuery.data?.band_counts
  const bandOptions = (Object.keys(scoring?.band_labels ?? {}) as KnowledgeReviewBand[]).map(
    (value) => ({
      value,
      label: `${scoring?.band_labels?.[value] ?? value}（${bandCounts?.[value] ?? 0}）`,
    }),
  )
  const scopeChips = useMemo(() => {
    const chips: string[] = []
    if (signal) chips.push(SIGNAL_META[signal]?.label ?? signal)
    if (productKey !== undefined) {
      const matched = facets?.products.find((item) => item.value === productKey)
      chips.push(matched?.label ?? (productKey || '未归类'))
    }
    if (kind) {
      chips.push(facets?.kinds.find((item) => item.value === kind)?.label ?? kind)
    }
    if (problemType) {
      chips.push(
        facets?.problem_types.find((item) => item.value === problemType)?.label ?? problemType,
      )
    }
    if (sourceType) {
      chips.push(
        facets?.source_types.find((item) => item.value === sourceType)?.label ?? sourceType,
      )
    }
    return chips
  }, [signal, productKey, kind, problemType, sourceType, facets])

  const hasCategoryFilter =
    productKey !== undefined || !!kind || !!problemType || !!sourceType

  // 批量动作的理由：**受控弹窗**而不是 modal.confirm。
  // confirm 的 content 是创建时就固定的元素，setState 不会重渲染它 ——
  // 用户选完理由看不到已选（这个坑实测踩过），所以理由必须放在受控组件里。
  const [batchMode, setBatchMode] = useState<'publish' | 'archive' | null>(null)

  const runBatch = (mode: 'publish' | 'archive', payload: { note: string; reasonCode?: string }) => {
    const ids = [...selectedIds]
    const { note, reasonCode } = payload
    const failed = (err: unknown) =>
      message.error(err instanceof Error ? err.message : '操作失败')
    const done = (text: string) => {
      setSelectedIds([])
      setBatchMode(null)
      message.success(text)
    }
    if (mode === 'publish') {
      batchPublish.mutate(
        { entryIds: ids, note: note || undefined, reasonCode },
        {
          onSuccess: (result) =>
            done(
              `已发布 ${result.published} 条${
                result.ai_generated ? `（含 AI 归纳 ${result.ai_generated} 条）` : ''
              }`,
            ),
          onError: failed,
        },
      )
      return
    }
    batchArchive.mutate(
      { entryIds: ids, note: note || undefined, reasonCode },
      {
        onSuccess: (result) => done(`已退回 ${result.archived} 条`),
        onError: failed,
      },
    )
  }

  // 理由码由后端下发（口径只有一处定义）。可选：不选也能提交，只是标签少一条信息。
  const reasonOptions = (action: 'publish' | 'archive') =>
    (reasonCodesQuery.data?.by_action?.[action] ?? []).map((code) => ({
      value: code,
      label: reasonCodesQuery.data?.labels?.[code] ?? code,
    }))

  // items 必须自己 memo：直接写 `?? []` 每次渲染都会产生新数组，
  // 让下面依赖它的 useMemo 每次都重算（eslint react-hooks 会告警）。
  const items = useMemo(() => queueQuery.data?.items ?? [], [queueQuery.data?.items])
  const selectedEntries = useMemo(
    () => items.filter((entry) => selectedIds.includes(entry.id)),
    [items, selectedIds],
  )
  // 选中的条目里有多少是 AI 归纳 —— 决定发布时是否强制填审核说明
  const selectedAiCount = selectedEntries.filter((entry) => entry.ai_generated).length
  // 「分数高的先审」：一键把当前页里"建议通过"的都勾上，人再点一次批量发布。
  // 刻意不做自动发布 —— 金标回测的 high 档精确率还远没到能自动的程度。
  const recommendedOnPage = useMemo(
    () => items.filter((entry) => entry.auto_band === 'high').length,
    [items],
  )
  const selectRecommended = () =>
    setSelectedIds(items.filter((entry) => entry.auto_band === 'high').map((entry) => entry.id))

  if (!canManage) {
    return (
      <Alert
        type="info"
        showIcon
        message="仅 manager / admin 可以审核知识条目"
        description="知识条目由多个来源自动聚合而来，进入客服问答库前必须经过人工审核。"
      />
    )
  }

  const columns: TableProps<KnowledgeEntry>['columns'] = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 360,
      render: (title: string, row) => (
        <Space size={4} wrap>
          {row.ai_generated && <Tag color="red">AI</Tag>}
          <Text
            style={{ cursor: 'pointer' }}
            onClick={() => setDetailEntry(row)}
          >
            {title}
          </Text>
        </Space>
      ),
    },
    {
      title: '审核线索',
      key: 'signals',
      width: 340,
      render: (_: unknown, row) => (
        <Space size={4} wrap>
          {row.qualified ? (
            <Tag color="green">三要素齐全</Tag>
          ) : (
            <Tooltip title={`缺：${(row.quality_missing || []).join('、')}`}>
              <Tag color="volcano">
                缺 {(row.quality_missing || []).length} 项
              </Tag>
            </Tooltip>
          )}
          {(row.review_signals || [])
            .filter((name) => name !== 'not_qualified')
            .map((name) => {
              const meta = SIGNAL_META[name]
              return (
                <Tag key={name} color={meta?.color ?? 'default'}>
                  {meta?.label ?? name}
                </Tag>
              )
            })}
          {(row.duplicate_group_size ?? 1) > 1 && (
            <Tag color="purple">同簇 {row.duplicate_group_size} 条</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '模型建议',
      key: 'auto_score',
      width: 130,
      // 分数必须能解释：鼠标悬停给出每一项加减分，否则没人会信这个数字
      render: (_: unknown, row) =>
        row.auto_band ? (
          <Tooltip
            title={
              <div>
                <div>
                  {row.auto_band_label}（{Math.round(row.auto_score ?? 0)} 分）
                  {row.auto_judge_label ? `：${row.auto_judge_label}` : ''}
                </div>
                {(row.auto_reasons || []).map((reason) => (
                  <div key={reason}>· {reason}</div>
                ))}
              </div>
            }
          >
            <Tag color={BAND_COLOR[row.auto_band]}>
              {row.auto_band_label} {Math.round(row.auto_score ?? 0)}
            </Tag>
          </Tooltip>
        ) : (
          <Text type="secondary">未打分</Text>
        ),
    },
    {
      title: '来源',
      dataIndex: 'source_type_label',
      key: 'source_type_label',
      width: 120,
    },
    {
      title: '产品',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 150,
      render: (name: string, row) =>
        row.product_key ? name : <Text type="secondary">{name || '未归类'}</Text>,
    },
    {
      title: '问题类型',
      dataIndex: 'problem_type_label',
      key: 'problem_type_label',
      width: 120,
      render: (label: string, row) => (
        <Tag color={KNOWLEDGE_PROBLEM_TYPE_COLOR[row.problem_type]}>{label || '未分类'}</Tag>
      ),
    },
    {
      title: '建议',
      dataIndex: 'suggested_action',
      key: 'suggested_action',
      width: 90,
      render: (action: string) => {
        const map: Record<string, { text: string; color: string }> = {
          publish: { text: '可发布', color: 'green' },
          archive: { text: '可退回', color: 'orange' },
          review: { text: '需人看', color: 'red' },
        }
        const meta = map[action] ?? { text: action, color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
      },
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Alert
        type="warning"
        showIcon
        message="审核通过才会进入客服问答库"
        description="知识条目由售后平台、禅道、企微会话、产品彩页与 RAGFlow 自动聚合而来。定时同步只推送**已发布**条目，因此审核是客服问答内容质量的唯一闸门。"
      />

      <Row gutter={16}>
        <Col span={4}>
          <Card size="small">
            <Statistic title="待审" value={summaryQuery.data?.pending_review ?? 0} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="三要素齐全"
              value={summaryQuery.data?.signal_counts?.publish_ready ?? 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="三要素不全"
              value={summaryQuery.data?.signal_counts?.not_qualified ?? 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="缺解决方案"
              value={summaryQuery.data?.signal_counts?.missing_solution ?? 0}
              valueStyle={{ color: '#d46b08' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="AI 待核对"
              value={summaryQuery.data?.signal_counts?.ai_unverified ?? 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="不同主题"
              value={summaryQuery.data?.distinct_title_groups ?? 0}
              suffix={`/ 重复簇 ${summaryQuery.data?.duplicate_clusters ?? 0}`}
            />
          </Card>
        </Col>
      </Row>

      <Space wrap>
        <Text type="secondary">分类：</Text>
        <Select
          allowClear
          placeholder="产品分类"
          style={{ minWidth: 190 }}
          value={productKey}
          options={[
            ...withCounts(facetsQuery.data?.products),
            ...(facetsQuery.data?.unclassified_product?.count
              ? [
                  {
                    value: '',
                    label: `${facetsQuery.data.unclassified_product.label}（${facetsQuery.data.unclassified_product.count}）`,
                  },
                ]
              : []),
          ]}
          onChange={(value) => {
            setProductKey(value ?? undefined)
            resetPaging()
          }}
        />
        <Select
          allowClear
          placeholder="知识形态"
          style={{ minWidth: 150 }}
          value={kind}
          options={withCounts(facetsQuery.data?.kinds)}
          onChange={(value) => {
            setKind(value ?? undefined)
            resetPaging()
          }}
        />
        <Select
          allowClear
          placeholder="问题类型"
          style={{ minWidth: 160 }}
          value={problemType}
          options={withCounts(facetsQuery.data?.problem_types)}
          onChange={(value) => {
            setProblemType(value ?? undefined)
            resetPaging()
          }}
        />
        <Select
          allowClear
          placeholder="来源类型"
          style={{ minWidth: 170 }}
          value={sourceType}
          options={withCounts(facetsQuery.data?.source_types)}
          onChange={(value) => {
            setSourceType(value ?? undefined)
            resetPaging()
          }}
        />
      </Space>

      {/* 打分分级：**只排序、只建议**。这个边界要写在页面上，不能靠口口相传 ——
          "高分自动发布"的诱惑很大，但当前金标回测还远没到能自动的程度。 */}
      <Alert
        type="info"
        showIcon
        message={
          <Space wrap size={6}>
            <Text strong>模型建议（打分器 {scoring?.version ?? '-'}）：</Text>
            <Tag color={BAND_COLOR.high}>建议通过 {bandCounts?.high ?? 0}</Tag>
            <Tag color={BAND_COLOR.mid}>待定 {bandCounts?.mid ?? 0}</Tag>
            <Tag color={BAND_COLOR.low}>建议退回 {bandCounts?.low ?? 0}</Tag>
            <Text type="secondary">
              {recommendedOnPage > 0
                ? `本页有 ${recommendedOnPage} 条建议通过，可一键勾选后批量发布`
                : '本页没有"建议通过"的条目'}
            </Text>
          </Space>
        }
        description={
          <span>
            队列已按分级排序（建议通过在最前）。分级来自"结论是不是已执行的处置动作"
            这条语义判据 + 可解释的加减分项，**只是建议**：发布仍由人点，
            自动发布尚未开启（要求人工金标回测达标，见
            <code>scripts/knowledge_score.py report</code>）。
            批量操作时选一个理由标签，会成为下一轮打分的金标 —— 这是提高分级准确率最快的办法。
          </span>
        }
      />

      <Space wrap>
        <Text type="secondary">按审核线索筛：</Text>
        <Select
          style={{ minWidth: 340 }}
          value={signal}
          options={SIGNAL_OPTIONS}
          onChange={(value) => {
            setSignal(value)
            resetPaging()
          }}
        />
        <Text type="secondary">按模型建议筛：</Text>
        <Select
          allowClear
          style={{ minWidth: 150 }}
          placeholder="全部分级"
          value={band}
          options={bandOptions}
          onChange={(value) => {
            setBand(value ?? undefined)
            resetPaging()
          }}
        />
        <Button size="small" onClick={selectRecommended} disabled={!recommendedOnPage}>
          全选本页「建议通过」（{recommendedOnPage}）
        </Button>
        <Button
          type="primary"
          disabled={!selectedIds.length}
          loading={batchPublish.isPending}
          onClick={() => setBatchMode('publish')}
        >
          批量发布{selectedIds.length ? `（${selectedIds.length}）` : ''}
        </Button>
        <Button
          danger
          disabled={!selectedIds.length}
          loading={batchArchive.isPending}
          onClick={() => setBatchMode('archive')}
        >
          批量退回{selectedIds.length ? `（${selectedIds.length}）` : ''}
        </Button>
        {selectedIds.length > 0 && (
          <Text type="secondary">
            已选 {selectedIds.length} 条{selectedAiCount > 0 ? `，其中 ${selectedAiCount} 条为 AI 归纳` : ''}
          </Text>
        )}
      </Space>

      {/* 当前审核范围：把"我在审哪一类"明确写在页面上，而不是让人从筛选框里猜 */}
      <Alert
        type={hasCategoryFilter || signal ? 'info' : 'warning'}
        showIcon
        message={
          <Space wrap size={6}>
            <Text strong>当前审核范围：</Text>
            {scopeChips.length === 0 ? (
              <Text>全部分类（未收窄）</Text>
            ) : (
              scopeChips.map((chip) => (
                <Tag key={chip} color="blue">
                  {chip}
                </Tag>
              ))
            )}
            <Text type="secondary">共 {queueQuery.data?.total ?? 0} 条待审</Text>
          </Space>
        }
        description={
          hasCategoryFilter || signal
            ? '分类条件与审核线索可叠加；改了任一条件会回到第一页并清空已勾选。'
            : '建议先在上面的「分类」里选一个产品/问题类型，把范围收窄到一类再审 —— 几千条混在一起时很难判断自己在审什么。'
        }
      />

      {queueQuery.isError ? (
        <ErrorResult error={queueQuery.error} onRetry={() => queueQuery.refetch()} />
      ) : (
        <Table
          size="small"
          rowKey="id"
          loading={queueQuery.isLoading}
          dataSource={items}
          columns={columns}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys as number[]),
            preserveSelectedRowKeys: false,
          }}
          pagination={{
            current: page,
            pageSize,
            total: queueQuery.data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条待审`,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage)
              setPageSize(nextSize)
              setSelectedIds([])
            },
          }}
        />
      )}

      {/* 批量发布 / 退回：理由标签可选（后端下发口径），选完立刻可见 */}
      <KnowledgeReviewNoteModal
        open={batchMode !== null}
        title={
          batchMode === 'publish'
            ? `发布 ${selectedIds.length} 条知识？`
            : `退回 ${selectedIds.length} 条知识？`
        }
        description={
          batchMode === 'publish'
            ? selectedAiCount > 0
              ? `其中 ${selectedAiCount} 条是 AI 二次梳理的产物。发布后进入客服问答库（发出去的是脱敏后的对外版本）。`
              : '发布后这些条目会进入客服问答库。'
            : '退回后这些条目不会进入客服问答库。'
        }
        // 不强制填理由（业务方要求不影响审核速度）；标签可选，但它是打分的金标来源
        required={false}
        confirmText={batchMode === 'publish' ? '发布' : '确定退回'}
        danger={batchMode === 'archive'}
        loading={batchPublish.isPending || batchArchive.isPending}
        reasonOptions={batchMode ? reasonOptions(batchMode) : []}
        onCancel={() => setBatchMode(null)}
        onSubmit={(payload) => batchMode && runBatch(batchMode, payload)}
      />

      <KnowledgeDetailDrawer
        entry={detailEntry}
        onClose={() => setDetailEntry(null)}
        onEdit={openEdit}
      />

      {/* 审核台就地编辑：与「知识条目」页共用同一个弹窗，字段与校验只有一处实现 */}
      <KnowledgeEditModal
        open={editOpen}
        entry={editingEntry}
        productOptions={withUnclassifiedProduct(productOptionsQuery.data?.items || [])}
        // 发布在审核台是受控动作（要确认、要写审核流水），不能在编辑弹窗里顺手勾掉；
        // 编辑模式下这个勾选框本来也不生效，露出来只会让人以为"勾了就发布了"。
        canPublishDirectly={false}
        onClose={() => {
          setEditOpen(false)
          setEditingEntry(null)
        }}
      />

      <Card size="small" title="审核建议（不是自动决定）">
        <Title level={5} style={{ marginTop: 0 }}>有用的知识必须同时满足三件事</Title>
        <ul style={{ marginBottom: 8 }}>
          <li><b>有发生的</b>：能看出这事真发生过（正文 `来源上下文` 有来源群/文档与时间）</li>
          <li><b>有问题分析</b>：针对该问题、针对该产品的分析（涉及哪个子系统、先查什么）</li>
          <li><b>有解决方案</b>：可复用的处理办法（<b>必须来自原始记录，不允许编造</b>）</li>
        </ul>
        <Text type="secondary">
          缺任意一项即「三要素不全」。实测 3533 条里只有 {summaryQuery.data?.signal_counts?.publish_ready ?? 0} 条齐全，
          {summaryQuery.data?.signal_counts?.not_qualified ?? 0} 条不全 —— 缺解决方案的条目**只能靠人工或新来源补**，
          大模型不能凭空补方案，因此它们不应被发布。
        </Text>
        <Title level={5}>建议的处理顺序</Title>
        <ol style={{ marginBottom: 0 }}>
          <li>
            <b>先清 AI 归纳（{summaryQuery.data?.signal_counts?.ai_unverified ?? 0} 条）</b>
            ：风险最高，必须逐条核对后才能发布。
          </li>
          <li>
            <b>批量发布「三要素齐全」（{summaryQuery.data?.signal_counts?.publish_ready ?? 0} 条）</b>
            ：已是可用知识。
          </li>
          <li>
            <b>对不合格条目先跑二次梳理</b>（`scripts/knowledge_curate.py`）：
            同一问题按点位复制的会被归并成一条，并补上"发生情况 + 问题分析"。
            实测 <code>--signal fixable</code>（有方案仅缺分析）梳理后 12/15 类合格。
          </li>
          <li>
            <b>批量退回仍缺方案（{summaryQuery.data?.signal_counts?.missing_solution ?? 0} 条）</b>
            ：来源侧本就没写处理办法，退回时统一写明理由，让现场回填。
          </li>
        </ol>
      </Card>
    </div>
  )
}
